<template>
  <div class="p-6 dark:text-white">
    <PageError v-if="error" :error="error" :errorCode="errorCode" />

    <template v-else-if="config">
      <div class="flex items-center justify-between mb-4">
        <h1 class="text-3xl font-bold">{{ config.name }}</h1>
        <div class="space-x-2">
          <span :class="[
            'px-3 py-1 text-sm rounded-full',
            config.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'
          ]">
            {{ config.enabled ? 'Enabled' : 'Disabled' }}
          </span>
          <span :class="[
            'px-3 py-1 text-sm rounded-full',
            config.pending ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
          ]">
            {{ config.pending ? 'Pending' : 'Approved' }}
          </span>
          <span v-if="config.delete_previous_files"
            class="px-3 py-1 text-sm rounded-full bg-red-100 text-red-800">
            Wipe pending
          </span>
        </div>
      </div>

      <!-- Tabs (mirrors ProbeDetails tab styling) -->
      <div class="glass-card mb-2">
        <nav class="flex px-2 py-2">
          <button
            @click="activeTab = 'config'"
            :class="[
              'ml-2 group inline-flex items-center px-4 py-2 border-t border-l border-r rounded-t-md text-sm font-medium transition-colors duration-150 ease-in-out',
              activeTab === 'config'
                ? 'bg-white text-blue-600 border-gray-300 -mb-px dark:bg-gray-800 dark:text-blue-400'
                : 'bg-gray-100 text-gray-500 border-transparent hover:bg-gray-50 hover:text-blue-600 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-blue-400'
            ]"
          >Configuration</button>
          <button
            v-if="isSuperuser"
            @click="onFilesTab"
            :class="[
              'ml-2 group inline-flex items-center px-4 py-2 border-t border-l border-r rounded-t-md text-sm font-medium transition-colors duration-150 ease-in-out',
              activeTab === 'files'
                ? 'bg-white text-blue-600 border-gray-300 -mb-px dark:bg-gray-800 dark:text-blue-400'
                : 'bg-gray-100 text-gray-500 border-transparent hover:bg-gray-50 hover:text-blue-600 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-blue-400'
            ]"
          >Files</button>
        </nav>
      </div>

      <!-- Shared full-width wrapper so Configuration and Files tabs render at
           identical widths. -->
      <div class="w-full">
        <!-- ========== CONFIGURATION TAB ========== -->
        <section
          v-if="activeTab === 'config'"
          class="w-full bg-white/80 dark:bg-gray-800/70 backdrop-blur-md p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
        >
          <!-- Probe status: live systemd state for the services this
               capture relies on (its dumpcap unit + the SPAN interface's
               offload service). Fetched on mount, reflects the probe as of
               now, not the cached list-view snapshot. -->
          <div
            v-if="probeStatus || probeStatusLoading || probeStatusError"
            class="mb-4 p-3 rounded-md border"
            :class="probeStatusBorderClass"
          >
            <div v-if="probeStatusLoading" class="flex items-center space-x-2 text-sm text-gray-500">
              <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 2.21.896 4.21 2.342 5.658l1.658-1.367z" />
              </svg>
              <span>Checking probe status...</span>
            </div>
            <div v-else-if="probeStatusError" class="text-sm text-gray-600">
              Could not reach probe ({{ probeStatusError }}).
            </div>
            <div v-else-if="probeStatus" class="text-sm">
              <div class="font-medium mb-2">Probe status</div>
              <ul class="space-y-1">
                <li
                  v-for="svc in probeStatus.services.details"
                  :key="svc.unit"
                  class="flex items-center space-x-2"
                >
                  <span class="inline-block w-2 h-2 rounded-full flex-shrink-0"
                    :class="serviceDotClass(svc.status, svc.flapping)"></span>
                  <span class="text-sm">{{ svc.display_name || svc.unit }}</span>
                  <span
                    v-if="svc.flapping"
                    class="text-[10px] uppercase tracking-wide bg-amber-200 text-amber-900 px-1 rounded"
                    :title="`Service has auto-restarted ${svc.n_restarts} time${svc.n_restarts === 1 ? '' : 's'} , likely crash-looping`"
                  >flapping</span>
                  <span class="ml-auto text-xs" :class="serviceTextClass(svc.status, svc.flapping)">
                    {{ serviceLabel(svc) }}
                  </span>
                  <button
                    v-if="canRestart(svc)"
                    type="button"
                    class="text-[11px] px-2 py-0.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                    :disabled="restartingUnits[svc.unit]"
                    @click="restartUnit(svc)"
                  >{{ restartingUnits[svc.unit] ? 'Restarting…' : 'Restart' }}</button>
                </li>
              </ul>
              <div v-if="restartNote" class="mt-2 text-xs" :class="restartNoteClass">
                {{ restartNote }}
              </div>
              <div class="mt-3 text-xs text-gray-600 dark:text-gray-400">
                This capture: <span class="font-mono">{{ thisCaptureOnDisk }}</span>
                in <code class="font-mono">/home/wireshark/captures-{{ config.id }}/</code>
              </div>
            </div>
          </div>

          <div class="space-y-3">
            <div><strong>Probe:</strong> {{ config.probe_name }}</div>

            <EditableField label="Name" fieldKey="name" :value="config.name"
              :editing="editingField === 'name'" :error="fieldErrors.name"
              @edit="startEdit('name')" @save="saveEdit" @cancel="cancelEdit" />

            <EditableField label="Interface" fieldKey="interface" :value="config.interface"
              :editing="editingField === 'interface'" :error="fieldErrors.interface"
              @edit="startEdit('interface')" @save="saveEdit" @cancel="cancelEdit" />

            <div class="flex items-start space-x-3">
              <span class="w-36 font-medium flex-shrink-0">Capture filter</span>
              <div class="flex-1">
                <code class="font-mono text-sm">{{ config.bpf_filter || '(none)' }}</code>
                <button type="button" class="ml-3 text-blue-600 hover:text-blue-800 text-xs"
                  @click="openBpfEditor">Edit</button>
              </div>
            </div>

            <EditableField label="File Prefix" fieldKey="file_prefix" :value="config.file_prefix"
              :editing="editingField === 'file_prefix'" :error="fieldErrors.file_prefix" mono
              @edit="startEdit('file_prefix')" @save="saveEdit" @cancel="cancelEdit" />

            <EditableField label="Filesize (KB)" fieldKey="filesize_kb" :value="config.filesize_kb" type="number"
              :editing="editingField === 'filesize_kb'" :error="fieldErrors.filesize_kb"
              @edit="startEdit('filesize_kb')" @save="saveEdit" @cancel="cancelEdit" />

            <EditableField label="File Count (ring depth)" fieldKey="file_count" :value="config.file_count" type="number"
              :editing="editingField === 'file_count'" :error="fieldErrors.file_count"
              @edit="startEdit('file_count')" @save="saveEdit" @cancel="cancelEdit" />

            <EditableField label="Rotate after (seconds)" fieldKey="rotate_duration_seconds"
              :value="config.rotate_duration_seconds" type="number"
              :editing="editingField === 'rotate_duration_seconds'" :error="fieldErrors.rotate_duration_seconds"
              @edit="startEdit('rotate_duration_seconds')" @save="saveEdit" @cancel="cancelEdit" />

            <EditableField label="Rotate on wall clock every (seconds)" fieldKey="rotate_interval_seconds"
              :value="config.rotate_interval_seconds" type="number"
              :editing="editingField === 'rotate_interval_seconds'" :error="fieldErrors.rotate_interval_seconds"
              @edit="startEdit('rotate_interval_seconds')" @save="saveEdit" @cancel="cancelEdit" />

            <EditableField label="Rotate after (packets)" fieldKey="rotate_packet_count"
              :value="config.rotate_packet_count" type="number"
              :editing="editingField === 'rotate_packet_count'" :error="fieldErrors.rotate_packet_count"
              @edit="startEdit('rotate_packet_count')" @save="saveEdit" @cancel="cancelEdit" />

            <EditableField label="Stop after (packets)" fieldKey="stop_after_packets"
              :value="config.stop_after_packets" type="number"
              :editing="editingField === 'stop_after_packets'" :error="fieldErrors.stop_after_packets"
              @edit="startEdit('stop_after_packets')" @save="saveEdit" @cancel="cancelEdit" />

            <EditableField label="Stop after (seconds)" fieldKey="stop_after_duration_seconds"
              :value="config.stop_after_duration_seconds" type="number"
              :editing="editingField === 'stop_after_duration_seconds'" :error="fieldErrors.stop_after_duration_seconds"
              @edit="startEdit('stop_after_duration_seconds')" @save="saveEdit" @cancel="cancelEdit" />

            <div><strong>Created:</strong> {{ formatDateTime(config.created_date) }}</div>
            <div><strong>Last Modified:</strong> {{ formatDateTime(config.last_modified) }}</div>
          </div>

          <!-- Delete previous files one-shot toggle -->
          <div class="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <label class="flex items-start space-x-3 cursor-pointer">
              <input type="checkbox" class="mt-1 h-4 w-4"
                :checked="config.delete_previous_files"
                @change="onToggleWipe($event)"
              />
              <div>
                <div class="font-medium">Delete previous capture files on next commit</div>
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  Wipes every pcap under <code>/home/wireshark/captures-{{ config.id }}/</code> on the probe.
                  One-shot. The flag clears automatically after the commit succeeds.
                </div>
              </div>
            </label>
          </div>

          <!-- Quick actions -->
          <div class="pt-6 flex space-x-2">
            <button @click="toggleEnabled" class="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700">
              {{ config.enabled ? 'Disable' : 'Enable' }}
            </button>
            <button @click="confirmDelete = true" class="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700">
              Delete
            </button>
          </div>
        </section>

        <!-- ========== FILES TAB ========== -->
        <section
          v-else-if="activeTab === 'files'"
          class="w-full bg-white/80 dark:bg-gray-800/70 backdrop-blur-md p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
        >
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl font-semibold">Pcap Files on Probe</h2>
            <button @click="refreshFiles" :disabled="loadingFiles"
              class="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2">
              <svg v-if="loadingFiles"
                class="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 2.21.896 4.21 2.342 5.658l1.658-1.367z" />
              </svg>
              <span>{{ loadingFiles ? 'Refreshing' : 'Refresh' }}</span>
            </button>
          </div>
          <div v-if="filesError" class="text-red-500 mb-2">{{ filesError }}</div>

          <div v-if="loadingFiles" class="flex flex-col items-center justify-center py-16">
            <svg class="animate-spin h-12 w-12 text-blue-500" xmlns="http://www.w3.org/2000/svg"
              fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 2.21.896 4.21 2.342 5.658l1.658-1.367z" />
            </svg>
            <div class="mt-4 text-sm text-gray-500 dark:text-gray-400">
              Enumerating pcap files on the probe…
            </div>
          </div>

          <div v-else-if="files.length === 0" class="text-gray-500 py-8 text-center">
            No files found on probe.
          </div>

          <table v-else class="min-w-full text-sm">
            <thead>
              <tr class="text-left border-b">
                <th class="px-2 py-2">Boot</th>
                <th class="px-2 py-2">Filename</th>
                <th class="px-2 py-2">Size</th>
                <th class="px-2 py-2">Modified</th>
                <th class="px-2 py-2">Rate limit</th>
                <th class="px-2 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="f in files" :key="f.id">
                <tr class="border-b border-gray-200 dark:border-gray-700">
                  <td class="px-2 py-2 font-mono text-xs">{{ f.boot_timestamp }}</td>
                  <td class="px-2 py-2 font-mono text-xs">{{ f.filename }}</td>
                  <td class="px-2 py-2">{{ formatBytes(f.size_bytes) }}</td>
                  <td class="px-2 py-2">{{ formatDateTime(f.mtime) }}</td>
                  <td class="px-2 py-2">
                    <GlassSelect
                      v-model="rateChoice[f.id]"
                      :options="downloadRateOptions"
                      valueKey="value"
                      labelKey="label"
                      placeholder="Rate"
                      :disabled="isJobActive(f.id)"
                    />
                  </td>
                  <td class="px-2 py-2">
                    <button
                      @click="startDownload(f)"
                      :disabled="isJobActive(f.id)"
                      class="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 disabled:opacity-50 inline-flex items-center space-x-1"
                    >
                      <svg v-if="isJobActive(f.id)" class="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 2.21.896 4.21 2.342 5.658l1.658-1.367z" />
                      </svg>
                      <span>{{ isJobActive(f.id) ? jobButtonLabel(f.id) : 'Download' }}</span>
                    </button>
                  </td>
                </tr>
                <tr v-if="downloadJobs[f.id]" class="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                  <td colspan="6" class="px-4 py-3">
                    <div v-if="downloadJobs[f.id].status === 'failed'" class="text-red-700 text-xs">
                      Transfer failed: {{ downloadJobs[f.id].error || 'unknown error' }}
                      <button class="ml-3 underline" @click="dismissJob(f.id)">dismiss</button>
                    </div>
                    <div v-else>
                      <div class="flex justify-between text-xs mb-1">
                        <span>{{ jobStatusText(f.id) }}</span>
                        <span class="font-mono">
                          {{ formatBytes(downloadJobs[f.id].bytes_transferred) }}
                          / {{ formatBytes(downloadJobs[f.id].total_bytes) }}
                          ({{ downloadJobs[f.id].pct }}%)
                        </span>
                      </div>
                      <div class="w-full bg-gray-200 dark:bg-gray-700 rounded h-2 overflow-hidden">
                        <div
                          class="h-2 bg-blue-500 transition-all"
                          :style="{ width: Math.min(downloadJobs[f.id].pct || 0, 100) + '%' }"
                        ></div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </section>
      </div>
    </template>

    <!-- Wipe confirm modal -->
    <div v-if="confirmWipe"
      @click.self="confirmWipe = false"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-xl border border-gray-200 dark:border-gray-800 w-[520px] max-w-[90%]">
        <div class="flex items-start space-x-3 mb-4">
          <svg class="w-8 h-8 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
          <div>
            <h3 class="text-lg font-semibold">Delete previous capture files?</h3>
            <p class="text-sm text-gray-600 dark:text-gray-300 mt-2">
              On the next commit, every pcap under
              <code>/home/wireshark/captures-{{ config && config.id }}/</code> on
              <b>{{ config && config.probe_name }}</b> will be permanently removed.
              This cannot be undone.
            </p>
          </div>
        </div>
        <div class="flex justify-end space-x-2">
          <button
            class="px-4 py-2 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-100"
            @click="confirmWipe = false"
          >Cancel</button>
          <button
            class="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700"
            @click="commitWipeToggle(true)"
          >Yes, mark for deletion</button>
        </div>
      </div>
    </div>

    <!-- BPF filter editor modal -->
    <div v-if="editingBpf"
      @click.self="editingBpf = false"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-xl border border-gray-200 dark:border-gray-800 w-[720px] max-w-[95%]">
        <h3 class="text-lg font-semibold mb-3">Edit capture filter</h3>
        <BpfFilterBuilder v-model="bpfDraft" />
        <div v-if="fieldErrors.bpf_filter" class="text-sm text-red-600 mt-3">{{ fieldErrors.bpf_filter }}</div>
        <div class="flex justify-end space-x-2 mt-5">
          <button
            class="px-4 py-2 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-100"
            @click="editingBpf = false"
          >Cancel</button>
          <button
            class="px-4 py-2 rounded-md bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50"
            :disabled="bpfSaving"
            @click="saveBpf"
          >{{ bpfSaving ? 'Saving...' : 'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- Delete capture confirm modal -->
    <div v-if="confirmDelete"
      @click.self="confirmDelete = false"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-xl border border-gray-200 dark:border-gray-800 w-[500px] max-w-[90%]">
        <h3 class="text-lg font-semibold mb-3">Delete capture config?</h3>
        <p class="text-sm text-gray-600 dark:text-gray-300 mb-4">
          <b>{{ config && config.name }}</b> will be marked for deletion and the dumpcap
          unit removed on the next commit. The pcap files on disk are preserved unless you
          also tick <b>Delete previous capture files</b>.
        </p>
        <div class="flex justify-end space-x-2">
          <button
            class="px-4 py-2 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-100"
            @click="confirmDelete = false"
          >Cancel</button>
          <button
            class="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700"
            @click="doDelete"
          >Delete</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { h, ref } from "vue";
import api from "@/services/api";
import {
  getCaptureConfigById,
  listCaptureFiles,
  buildCaptureDownloadUrl,
  deleteCaptureConfig,
  updateCaptureConfig,
  getProbeTelemetry,
  prepareDownload,
  getDownloadJobStatus,
  restartProbeService,
} from "@/services/captureService";
import PageError from "@/components/PageError.vue";
import BpfFilterBuilder from "@/components/BpfFilterBuilder.vue";
import GlassSelect from "@/components/GlassSelect.vue";
import { formatError } from "@/utils/errorMessages";
import { useStore } from "@/store";
import { useMultiTenant } from "@/composables/useMultiTenant";
import { formatBytes } from "@/utils/formatBytes";

// Inline editable field component. Click Edit to swap to input, Save/Cancel
// buttons inline. Emits 'edit', 'save' (with new value), 'cancel'.
const EditableField = {
  name: "EditableField",
  props: {
    label: { type: String, required: true },
    fieldKey: { type: String, required: true },
    value: { default: "" },
    editing: { type: Boolean, default: false },
    error: { type: String, default: "" },
    type: { type: String, default: "text" },
    mono: { type: Boolean, default: false },
  },
  emits: ["edit", "save", "cancel"],
  setup(props, { emit }) {
    const draft = ref(props.value);
    const startingValue = ref(props.value);

    const sync = () => {
      draft.value = props.value;
      startingValue.value = props.value;
    };

    return () => {
      if (!props.editing) {
        return h("div", { class: "flex items-center justify-between group" }, [
          h("div", [
            h("strong", {}, `${props.label}: `),
            h(
              props.mono ? "code" : "span",
              { class: props.mono ? "font-mono text-sm" : "" },
              (props.value === "" || props.value === null || props.value === undefined)
                ? "(none)"
                : String(props.value),
            ),
          ]),
          h(
            "button",
            {
              class:
                "text-blue-600 dark:text-blue-400 hover:text-blue-800 text-xs",
              onClick: () => {
                sync();
                emit("edit");
              },
            },
            "Edit",
          ),
        ]);
      }

      return h("div", {}, [
        h("div", { class: "flex items-center space-x-2" }, [
          h("strong", { class: "whitespace-nowrap" }, `${props.label}: `),
          h("input", {
            class:
              "flex-1 border border-gray-300 dark:bg-gray-700 rounded px-2 py-1 font-mono text-sm",
            type: props.type,
            value: draft.value,
            onInput: (e) => (draft.value = e.target.value),
            onKeydown: (e) => {
              if (e.key === "Enter") emit("save", { field: props.fieldKey, value: draft.value });
              if (e.key === "Escape") emit("cancel", props.fieldKey);
            },
          }),
          h(
            "button",
            {
              class:
                "bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700",
              onClick: () => emit("save", { field: props.fieldKey, value: draft.value }),
            },
            "Save",
          ),
          h(
            "button",
            {
              class:
                "bg-gray-300 text-gray-800 px-3 py-1 rounded text-xs hover:bg-gray-400 dark:bg-gray-600 dark:text-gray-100",
              onClick: () => emit("cancel", props.fieldKey),
            },
            "Cancel",
          ),
        ]),
        props.error
          ? h("div", { class: "text-red-500 text-xs mt-1 ml-[calc(var(--label-w,0))]" }, props.error)
          : null,
      ]);
    };
  },
};

export default {
  name: "CaptureDetails",
  components: { PageError, EditableField, BpfFilterBuilder, GlassSelect },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      config: null,
      activeTab: "config",
      files: [],
      loadingFiles: false,
      filesError: "",
      error: "",
      errorCode: "",
      editingField: null,
      fieldErrors: {},
      confirmWipe: false,
      confirmDelete: false,
      probeStatus: null,
      probeStatusLoading: false,
      probeStatusError: "",
      // Per-file download state. rateChoice maps file_id -> KB/s (0 = no
      // limit). downloadJobs maps file_id -> latest job status payload.
      // pollTimers maps file_id -> setInterval handle for cleanup.
      rateChoice: {},
      downloadJobs: {},
      pollTimers: {},
      editingBpf: false,
      bpfDraft: "",
      bpfSaving: false,
      // Restart UX state. restartingUnits tracks which units have an in-flight
      // restart call. restartNote is an inline status line under the service
      // list ("Restart attempted…" / "Service came back then failed again…").
      restartingUnits: {},
      restartNote: "",
      restartNoteKind: "info", // "info" | "ok" | "warn" | "error"
    };
  },
  computed: {
    user() { return useStore.getUser(); },
    isSuperuser() { return !!this.user?.is_superuser; },
    downloadRateOptions() {
      return [
        { value: 100, label: '100 Kb/s (recommended)' },
        { value: 50, label: '50 Kb/s (very slow)' },
        { value: 500, label: '500 Kb/s' },
        { value: 0, label: 'Full speed (no limit)' },
      ];
    },
    probeStatusBorderClass() {
      if (this.probeStatusError) return "border-gray-300 bg-gray-50 dark:bg-gray-800 dark:border-gray-700";
      if (!this.probeStatus) return "border-gray-300 bg-gray-50 dark:bg-gray-800 dark:border-gray-700";
      const s = this.probeStatus.services;
      if (s.failed > 0) return "border-red-400 bg-red-50 dark:bg-red-950 dark:border-red-700";
      // Flapping or user-disabled-but-still-running shows a softer warning
      // band , real problem worth a look, but not a production outage.
      const anyFlapping = (s.details || []).some(d => d.flapping);
      if ((s.stopping || 0) > 0 || anyFlapping) {
        return "border-amber-400 bg-amber-50 dark:bg-amber-950 dark:border-amber-700";
      }
      return "border-gray-300 bg-gray-50 dark:bg-gray-800 dark:border-gray-700";
    },
    restartNoteClass() {
      switch (this.restartNoteKind) {
        case "ok": return "text-green-700 dark:text-green-400";
        case "warn": return "text-amber-700 dark:text-amber-400";
        case "error": return "text-red-700 dark:text-red-400";
        default: return "text-gray-600 dark:text-gray-400";
      }
    },
    thisCaptureOnDisk() {
      if (!this.probeStatus || !this.config) return "0 B";
      const perPk = this.probeStatus?.disk?.captures_per_pk || {};
      const bytes = Number(perPk[String(this.config.id)] || 0);
      return this.formatBytes(bytes);
    },
  },
  async mounted() {
    await this.tenant.fetchOrganizations();
    if (this.tenant.currentOrganizationId.value) {
      // Org already pinned (back-navigation or ?client= refresh):
      // loadInitialData() skips the fetch when the org is unchanged.
      await this.tenant.syncUrlParam();
      await this.fetchConfig(this.tenant.currentSchemaName.value);
    } else {
      await this.tenant.loadInitialData(this.fetchConfig);
    }
    this.fetchProbeStatus();
  },
  beforeUnmount() {
    // Cancel any in-flight poll loops on navigate-away.
    for (const t of Object.values(this.pollTimers || {})) {
      if (t) clearInterval(t);
    }
  },
  methods: {
    async fetchConfig(schemaName = null) {
      try {
        this.config = await getCaptureConfigById(this.$route.params.id, schemaName);
      } catch (err) {
        const { code, message } = formatError(err);
        this.errorCode = code;
        this.error = message;
      }
    },
    async fetchProbeStatus() {
      if (!this.config || !this.config.probe) return;
      this.probeStatusLoading = true;
      this.probeStatusError = "";
      try {
        this.probeStatus = await getProbeTelemetry(this.config.probe, {
          refresh: true,
          schemaName: this.tenant.currentSchemaName?.value,
        });
      } catch (e) {
        this.probeStatus = null;
        this.probeStatusError = e?.response?.data?.error || e.message || "unreachable";
      } finally {
        this.probeStatusLoading = false;
      }
    },
    serviceDotClass(status, flapping) {
      if (flapping) return "bg-amber-500";
      if (status === "running") return "bg-green-500";
      if (status === "completed") return "bg-green-400";
      if (status === "stopped_by_user") return "bg-gray-400";
      if (status === "not_yet_committed") return "bg-blue-400";
      if (status === "stopping") return "bg-amber-500";
      return "bg-red-500";
    },
    serviceTextClass(status, flapping) {
      if (flapping) return "text-amber-700 dark:text-amber-400";
      if (status === "running") return "text-green-700 dark:text-green-400";
      if (status === "completed") return "text-green-700 dark:text-green-400";
      if (status === "stopped_by_user") return "text-gray-500 dark:text-gray-400";
      if (status === "not_yet_committed") return "text-blue-700 dark:text-blue-400";
      if (status === "stopping") return "text-amber-700 dark:text-amber-400";
      return "text-red-700 dark:text-red-400";
    },
    serviceLabel(svc) {
      if (svc.status === "running") return "active";
      if (svc.status === "completed") return "completed (stop condition fired)";
      if (svc.status === "stopped_by_user") return "stopped by user";
      if (svc.status === "not_yet_committed") return "not yet committed";
      if (svc.status === "stopping") return "stopping (commit pending)";
      if (svc.active === "activating" || svc.sub_state === "auto-restart") return "restart looping";
      // A genuine failure worth escalating: auto-restart has given up
      // (exceeded burst) or exit was signal/non-zero outside a stop cond.
      return `failed (${svc.result || svc.active}), needs attention`;
    },
    canRestart(svc) {
      // Only show Restart when intervention is actually needed: failed
      // starts, flapping units, or stuck "stopping" states. Hide on healthy
      // running services so a curious click doesn't interrupt a live
      // capture. Not-yet-committed has no unit to restart. "Completed"
      // is the capture doing what the user configured it to do, so don't
      // offer a restart that would just re-trigger the stop condition.
      if (svc.kind !== "capture" && svc.kind !== "offload") return false;
      if (svc.status === "not_yet_committed") return false;
      if (svc.status === "stopped_by_user") return false;
      if (svc.status === "completed") return false;
      if (svc.status === "running" && !svc.flapping) return false;
      return true;
    },
    async restartUnit(svc) {
      this.restartingUnits = { ...this.restartingUnits, [svc.unit]: true };
      this.restartNote = `Restart requested for ${svc.display_name || svc.unit}…`;
      this.restartNoteKind = "info";
      try {
        await restartProbeService(this.config.probe, svc.unit, {
          schemaName: this.tenant.currentSchemaName?.value,
        });
      } catch (e) {
        const msg = e?.response?.data?.error || e.message || "Restart failed";
        this.restartNote = `Restart failed: ${msg}`;
        this.restartNoteKind = "error";
        this.restartingUnits = { ...this.restartingUnits, [svc.unit]: false };
        return;
      }
      // Re-poll telemetry twice: once shortly after restart (is it up?), then
      // again after a longer pause (did it stay up?). If the unit comes up
      // briefly and then dies or flaps, the second poll catches it.
      try {
        await this.sleep(3000);
        await this.fetchProbeStatus();
        const afterFirst = this.findService(svc.unit);
        await this.sleep(10000);
        await this.fetchProbeStatus();
        const afterSecond = this.findService(svc.unit);
        const firstOk = afterFirst && afterFirst.status === "running";
        const secondOk = afterSecond && afterSecond.status === "running";
        const secondFlapping = afterSecond && afterSecond.flapping;
        if (firstOk && !secondOk) {
          this.restartNote = `${svc.display_name || svc.unit} came up briefly then failed again , needs attention.`;
          this.restartNoteKind = "warn";
        } else if (secondFlapping) {
          this.restartNote = `${svc.display_name || svc.unit} restarted but is flapping (${afterSecond.n_restarts} crash-restarts) , needs attention.`;
          this.restartNoteKind = "warn";
        } else if (secondOk) {
          this.restartNote = `${svc.display_name || svc.unit} restarted successfully.`;
          this.restartNoteKind = "ok";
        } else {
          this.restartNote = `${svc.display_name || svc.unit} did not come up after restart , needs attention.`;
          this.restartNoteKind = "error";
        }
      } finally {
        this.restartingUnits = { ...this.restartingUnits, [svc.unit]: false };
      }
    },
    findService(unit) {
      return (this.probeStatus?.services?.details || []).find(s => s.unit === unit);
    },
    sleep(ms) {
      return new Promise(r => setTimeout(r, ms));
    },
    startEdit(field) {
      this.editingField = field;
      this.fieldErrors = { ...this.fieldErrors, [field]: "" };
    },
    cancelEdit() {
      this.editingField = null;
      this.fieldErrors = {};
    },
    async saveEdit({ field, value }) {
      const NUMERIC_FIELDS = new Set([
        "filesize_kb", "file_count",
        "rotate_duration_seconds", "rotate_interval_seconds", "rotate_packet_count",
        "stop_after_packets", "stop_after_duration_seconds",
      ]);
      let payload = value;
      if (NUMERIC_FIELDS.has(field)) {
        // Allow blank to clear the field (sends null).
        if (value === "" || value == null) {
          payload = null;
        } else {
          payload = parseInt(value, 10);
          if (Number.isNaN(payload)) {
            this.fieldErrors = { ...this.fieldErrors, [field]: "Must be a number, or blank." };
            return;
          }
        }
      }
      try {
        await updateCaptureConfig(
          this.config.id,
          { [field]: payload },
          this.tenant.currentSchemaName?.value,
        );
        await this.fetchConfig(this.tenant.currentSchemaName?.value);
        this.editingField = null;
        this.fieldErrors = {};
      } catch (err) {
        const data = err?.response?.data;
        if (data && typeof data === "object" && data[field]) {
          const msg = Array.isArray(data[field]) ? data[field].join(" ") : String(data[field]);
          this.fieldErrors = { ...this.fieldErrors, [field]: msg };
        } else {
          this.fieldErrors = { ...this.fieldErrors, [field]: "Save failed." };
        }
      }
    },
    onToggleWipe(event) {
      const checked = event.target.checked;
      if (checked) {
        // Revert the box visually; confirm modal has to green-light the change.
        event.target.checked = false;
        this.confirmWipe = true;
      } else {
        // Unticking doesn't need confirmation (user is cancelling the request).
        this.commitWipeToggle(false);
      }
    },
    async commitWipeToggle(value) {
      this.confirmWipe = false;
      try {
        await updateCaptureConfig(
          this.config.id,
          { delete_previous_files: value },
          this.tenant.currentSchemaName?.value,
        );
        await this.fetchConfig(this.tenant.currentSchemaName?.value);
      } catch (e) {
        alert("Failed to update delete-previous-files flag.");
      }
    },
    async onFilesTab() {
      this.activeTab = "files";
      if (this.files.length === 0) await this.refreshFiles();
    },
    async refreshFiles() {
      this.loadingFiles = true;
      this.filesError = "";
      try {
        this.files = await listCaptureFiles(this.$route.params.id, this.tenant.currentSchemaName?.value);
        // Seed the rate-limit select so each row shows 100 Kb/s by default
        // instead of a blank option. Preserves any choice the user has
        // already made (re-refresh doesn't clobber their picks).
        const seeded = { ...this.rateChoice };
        for (const f of this.files) {
          if (seeded[f.id] === undefined) seeded[f.id] = 100;
        }
        this.rateChoice = seeded;
      } catch (e) {
        this.filesError = e?.response?.data?.error || "Failed to list files.";
      } finally {
        this.loadingFiles = false;
      }
    },
    // ----- Async download flow -----
    // Click Download -> POST .../prepare/ with rate -> get job_id -> poll
    // every 2s -> on done, GET the existing X-Accel-Redirect URL with
    // ?job_id= -> save the response blob.
    isJobActive(fileId) {
      const job = this.downloadJobs[fileId];
      if (!job) return false;
      return job.status === "queued" || job.status === "running";
    },
    jobButtonLabel(fileId) {
      const job = this.downloadJobs[fileId];
      if (!job) return "Download";
      if (job.status === "queued") return "Queued...";
      return `${job.pct || 0}%`;
    },
    jobStatusText(fileId) {
      const job = this.downloadJobs[fileId];
      if (!job) return "";
      if (job.status === "queued") return "Queued; waiting for probe to start upload...";
      if (job.status === "running") return "Uploading from probe...";
      if (job.status === "done") return "Upload complete; starting browser download...";
      return job.status;
    },
    dismissJob(fileId) {
      this.stopPolling(fileId);
      this.downloadJobs = { ...this.downloadJobs, [fileId]: undefined };
    },
    stopPolling(fileId) {
      const t = this.pollTimers[fileId];
      if (t) {
        clearInterval(t);
        this.pollTimers = { ...this.pollTimers, [fileId]: undefined };
      }
    },
    async startDownload(f) {
      const rate = this.rateChoice[f.id] === undefined ? 100 : this.rateChoice[f.id];
      const rateKbps = rate > 0 ? rate : null;
      try {
        const { job_id } = await prepareDownload(this.$route.params.id, f.id, {
          rateKbps,
          schemaName: this.tenant.currentSchemaName?.value,
        });
        // Initialise the job UI to "queued" with zero bytes immediately so
        // the button flips into spinner state without waiting for the first
        // poll.
        this.downloadJobs = {
          ...this.downloadJobs,
          [f.id]: {
            id: job_id,
            status: "queued",
            bytes_transferred: 0,
            total_bytes: f.size_bytes || 0,
            pct: 0,
            error: "",
          },
        };
        this.pollTimers = {
          ...this.pollTimers,
          [f.id]: setInterval(() => this.pollJob(f, job_id), 2000),
        };
      } catch (e) {
        const msg = e?.response?.data?.error || e.message || "Failed to start download";
        this.downloadJobs = {
          ...this.downloadJobs,
          [f.id]: { status: "failed", error: msg, pct: 0, bytes_transferred: 0, total_bytes: 0 },
        };
      }
    },
    async pollJob(f, jobId) {
      try {
        const status = await getDownloadJobStatus(jobId, {
          schemaName: this.tenant.currentSchemaName?.value,
        });
        this.downloadJobs = { ...this.downloadJobs, [f.id]: status };
        if (status.status === "done") {
          this.stopPolling(f.id);
          await this.fetchAndSaveBlob(f, jobId);
          // After the browser save, clear the row after a short delay so
          // the user has time to read the "complete" indicator.
          setTimeout(() => this.dismissJob(f.id), 4000);
        } else if (status.status === "failed") {
          this.stopPolling(f.id);
        }
      } catch (e) {
        // Transient poll failure; ignore (next tick will retry). On 404
        // (job purged) we stop.
        if (e?.response?.status === 404) {
          this.stopPolling(f.id);
          this.downloadJobs = {
            ...this.downloadJobs,
            [f.id]: { status: "failed", error: "Job not found", pct: 0, bytes_transferred: 0, total_bytes: 0 },
          };
        }
      }
    },
    async fetchAndSaveBlob(f, jobId) {
      try {
        const url = buildCaptureDownloadUrl(this.$route.params.id, f.id, this.tenant.currentSchemaName?.value);
        const sep = url.includes("?") ? "&" : "?";
        const response = await api.get(`${url}${sep}job_id=${jobId}`, { responseType: "blob" });
        const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/vnd.tcpdump.pcap" }));
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = f.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
      } catch (e) {
        const msg = e?.response?.data?.error || e.message || "Failed to download";
        this.downloadJobs = {
          ...this.downloadJobs,
          [f.id]: { status: "failed", error: msg, pct: 0, bytes_transferred: 0, total_bytes: 0 },
        };
      }
    },
    async toggleEnabled() {
      try {
        await updateCaptureConfig(
          this.config.id,
          { enabled: !this.config.enabled },
          this.tenant.currentSchemaName?.value,
        );
        await this.fetchConfig(this.tenant.currentSchemaName?.value);
      } catch (e) {
        alert("Failed to toggle capture state.");
      }
    },
    async doDelete() {
      this.confirmDelete = false;
      try {
        await deleteCaptureConfig(this.config.id, this.tenant.currentSchemaName?.value);
        this.$router.push({ name: "CaptureView", query: this.tenant.addSchemaParam() });
      } catch (e) {
        alert("Delete failed.");
      }
    },
    formatDateTime(v) {
      if (!v) return "";
      return new Date(v).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    },
    formatBytes,
    openBpfEditor() {
      this.bpfDraft = this.config.bpf_filter || "";
      this.fieldErrors.bpf_filter = "";
      this.editingBpf = true;
    },
    async saveBpf() {
      this.bpfSaving = true;
      this.fieldErrors.bpf_filter = "";
      try {
        await updateCaptureConfig(
          this.config.id,
          { bpf_filter: this.bpfDraft },
          this.tenant.currentSchemaName?.value,
        );
        this.config.bpf_filter = this.bpfDraft;
        this.editingBpf = false;
      } catch (err) {
        const data = err?.response?.data;
        if (data && typeof data === "object" && data.bpf_filter) {
          this.fieldErrors.bpf_filter = Array.isArray(data.bpf_filter)
            ? data.bpf_filter.join(" ")
            : String(data.bpf_filter);
        } else {
          this.fieldErrors.bpf_filter = "Save failed. Check the filter and try again.";
        }
      } finally {
        this.bpfSaving = false;
      }
    },
  },
};
</script>
